import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useStudentTracking } from '../hooks/useStudentTracking';
import { useEditTracking } from '../hooks/useEditTracking';
import Sidebar from './Sidebar';
import Navbar from './Navbar';
import PatientNav from './PatientNav';
import withRoleGuard from '../hoc/withRoleGuard';
import { FaFlask, FaUniversity, FaBuilding, FaCheck, FaTimes, FaArrowLeft } from 'react-icons/fa';

const PatientLab = () => {
  const { nationalId } = useParams();
  const { token, user } = useAuth();
  const { trackPageView, trackButtonClick, trackDataOperation, trackError } = useStudentTracking();
  const { trackStatusChange } = useEditTracking();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [patientData, setPatientData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [labType, setLabType] = useState(''); // 'university' or 'outside'
  const [formType, setFormType] = useState(''); // 'fixed' or 'removable'
  const [subFormType, setSubFormType] = useState(''); // For fixed: 'primary', 'final', 'tryin' | For removable: 'complete', 'rpd'
  const [submitting, setSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [labRequests, setLabRequests] = useState([]);

  // Form data states
  const [fixedPrimaryForm, setFixedPrimaryForm] = useState({
    tooth: '',
    laboratorySteps: {
      mountedDiagnosticCasts: false,
      diagnosticWaxUp: false,
      provisionalRestoration: false
    },
    notes: ''
  });

  const [fixedFinalForm, setFixedFinalForm] = useState({
    tooth: '',
    design: '',
    material: '',
    pontic: '',
    color: '',
    notes: ''
  });

  const [fixedTryInForm, setFixedTryInForm] = useState({
    tooth: '',
    tryInOk: false,
    problemWithTryIn: false,
    remarks: ''
  });

  const [removableCompleteForm, setRemovableCompleteForm] = useState({
    laboratorySteps: {
      studyCastsAndCustomTrays: false,
      masterCastsAndRecordBlocks: false,
      settingOfTeeth: false,
      finalProcessedPolishedDenture: false
    },
    notes: ''
  });

  const [removableRpdForm, setRemovableRpdForm] = useState({
    laboratorySteps: {
      studyCastsAndCustomTrays: false,
      masterCastsAndMetalFramework: false,
      settingOfTeeth: false,
      rpdTryIn: false,
      finalProcessedPolishedDenture: false
    },
    notes: ''
  });

  useEffect(() => {
    trackPageView('patient_lab', {
      patientId: nationalId,
      userId: user?.studentId || user?.id,
      userName: user?.name
    });
    fetchPatientData();
    fetchLabRequests();
  }, [nationalId]);

  const fetchPatientData = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/patients/public/${nationalId}`);
      setPatientData(response.data);
    } catch (err) {
      console.error('Error fetching patient data:', err);
      setError(err.response?.data?.message || 'Failed to load patient data');
    } finally {
      setLoading(false);
    }
  };

  const fetchLabRequests = async () => {
    try {
      if (!token) return;
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/lab-requests/student`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const patientRequests = response.data.filter(req => req.patientId === nationalId);
      setLabRequests(patientRequests);
    } catch (error) {
      console.error('Error fetching lab requests:', error);
    }
  };

  const handleLabTypeSelect = (type) => {
    setLabType(type);
    setFormType('');
    setSubFormType('');
    trackButtonClick('lab_type_select', { labType: type });
  };

  const handleFormTypeSelect = (type) => {
    setFormType(type);
    setSubFormType('');
    trackButtonClick('lab_form_type_select', { formType: type, labType: labType });
  };

  const handleSubFormTypeSelect = (type) => {
    setSubFormType(type);
    trackButtonClick('lab_subform_type_select', { 
      formType: formType, 
      subFormType: type,
      labType: labType 
    });
  };

  const handleSubmit = async () => {
    if (!labType || !formType || !subFormType) return;

    setSubmitting(true);
    try {
      let formData = {};
      
      if (formType === 'fixed') {
        if (subFormType === 'primary') {
          formData = {
            formType: 'fixed_prosthodontics_primary',
            ...fixedPrimaryForm
          };
        } else if (subFormType === 'final') {
          formData = {
            formType: 'fixed_prosthodontics_final',
            ...fixedFinalForm
          };
        } else if (subFormType === 'tryin') {
          formData = {
            formType: 'fixed_prosthodontics_tryin',
            ...fixedTryInForm
          };
        }
      } else if (formType === 'removable') {
        if (subFormType === 'complete') {
          formData = {
            formType: 'removable_prosthodontics_complete',
            ...removableCompleteForm
          };
        } else if (subFormType === 'rpd') {
          formData = {
            formType: 'removable_prosthodontics_rpd',
            ...removableRpdForm
          };
        }
      }

      const requestData = {
        patientId: nationalId,
        patientName: patientData.fullName,
        labType: labType,
        formData: formData,
        studentId: user?.studentId || user?.id,
        studentName: user?.name
      };

      await axios.post(`${process.env.REACT_APP_API_URL}/api/lab-requests`, requestData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      trackDataOperation('create', 'lab_request', 'success', {
        labType: labType,
        formType: formType,
        subFormType: subFormType
      });

      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        resetForm();
        fetchLabRequests();
      }, 2000);
    } catch (error) {
      console.error('Error submitting lab request:', error);
      trackError('lab_request_submit_error', error.message, {
        labType: labType,
        formType: formType
      });
      alert('Error submitting lab request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setLabType('');
    setFormType('');
    setSubFormType('');
    setFixedPrimaryForm({
      tooth: '',
      laboratorySteps: {
        mountedDiagnosticCasts: false,
        diagnosticWaxUp: false,
        provisionalRestoration: false
      },
      notes: ''
    });
    setFixedFinalForm({
      tooth: '',
      design: '',
      material: '',
      pontic: '',
      color: '',
      notes: ''
    });
    setFixedTryInForm({
      tooth: '',
      tryInOk: false,
      problemWithTryIn: false,
      remarks: ''
    });
    setRemovableCompleteForm({
      laboratorySteps: {
        studyCastsAndCustomTrays: false,
        masterCastsAndRecordBlocks: false,
        settingOfTeeth: false,
        finalProcessedPolishedDenture: false
      },
      notes: ''
    });
    setRemovableRpdForm({
      laboratorySteps: {
        studyCastsAndCustomTrays: false,
        masterCastsAndMetalFramework: false,
        settingOfTeeth: false,
        rpdTryIn: false,
        finalProcessedPolishedDenture: false
      },
      notes: ''
    });
  };

  const formatDate = (dateValue) => {
    try {
      let date;
      if (typeof dateValue === 'string') {
        date = new Date(dateValue);
      } else if (dateValue && dateValue.toDate) {
        date = dateValue.toDate();
      } else if (dateValue instanceof Date) {
        date = dateValue;
      } else if (dateValue && dateValue._seconds) {
        date = new Date(dateValue._seconds * 1000);
      } else {
        return null;
      }
      if (isNaN(date.getTime())) {
        return null;
      }
      return date;
    } catch (error) {
      console.error('Error parsing date:', error);
      return null;
    }
  };

  const formatDateString = (dateValue) => {
    const date = formatDate(dateValue);
    if (!date) return 'Date not available';
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="flex h-screen bg-gray-50">
        <Sidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />
        <div className="flex-1 flex flex-col overflow-hidden">
          <Navbar toggleSidebar={() => setSidebarOpen(!sidebarOpen)} />
          <PatientNav />
          <main className="flex-1 overflow-y-auto p-6 bg-gradient-to-br from-[rgba(0,119,182,0.05)] to-white">
            <div className="max-w-7xl mx-auto">
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8">
                <div className="animate-pulse">
                  <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
                  <div className="h-64 bg-gray-200 rounded"></div>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  if (error || !patientData) {
    return (
      <div className="flex h-screen bg-gray-50">
        <Sidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />
        <div className="flex-1 flex flex-col overflow-hidden">
          <Navbar toggleSidebar={() => setSidebarOpen(!sidebarOpen)} />
          <PatientNav />
          <main className="flex-1 overflow-y-auto p-6">
            <div className="max-w-7xl mx-auto">
              <div className="bg-white rounded-xl shadow-sm border border-red-200 p-8">
                <p className="text-red-600">{error || 'Patient not found'}</p>
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Navbar toggleSidebar={() => setSidebarOpen(!sidebarOpen)} />
        <PatientNav />
        <main className="flex-1 overflow-y-auto p-4 sm:p-6 bg-gradient-to-br from-blue-50 to-white">
          <div className="max-w-7xl mx-auto">
            {/* Header */}
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6"
            >
              <h1 className="text-2xl sm:text-3xl font-bold text-[#0077B6]">Laboratory Requests</h1>
              <p className="text-gray-600 mt-1">
                Submit lab requests for {patientData?.fullName || 'this patient'}
              </p>
            </motion.div>

            {/* Main Content Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden"
            >
              {/* Card Header with Back Button */}
              <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-100">
                <div className="flex items-center">
                  <FaFlask className="h-6 w-6 text-[#0077B6] mr-3" />
                  <span className="font-semibold text-gray-800">
                    {!labType ? 'Select Lab Type' : 
                     labType === 'university' && !formType ? 'Select Form Type' :
                     labType === 'university' && formType && !subFormType ? `Select ${formType === 'fixed' ? 'Fixed' : 'Removable'} Form` :
                     'Lab Request Form'}
                  </span>
                </div>
                {labType && (
                  <button
                    onClick={() => {
                      resetForm();
                      trackButtonClick('lab_reset_form');
                    }}
                    className="flex items-center px-3 py-1.5 text-sm text-gray-600 hover:text-[#0077B6] hover:bg-gray-50 rounded-lg transition-colors"
                  >
                    <FaArrowLeft className="h-3 w-3 mr-2" />
                    Back
                  </button>
                )}
              </div>

              <div className="p-4 sm:p-6">

              {/* Success Message */}
              {showSuccess && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-6 p-4 bg-green-100 border border-green-300 rounded-lg text-green-800"
                >
                  <div className="flex items-center">
                    <FaCheck className="h-5 w-5 mr-2" />
                    Lab request submitted successfully!
                  </div>
                </motion.div>
              )}

              {/* Lab Type Selection */}
              {!labType && (
                <div className="grid grid-cols-2 gap-3 mb-6">
                  <motion.div
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.99 }}
                    className="bg-blue-50 p-4 rounded-lg border border-blue-200 cursor-pointer transition-all duration-200 hover:shadow-md hover:border-blue-400"
                    onClick={() => handleLabTypeSelect('university')}
                  >
                    <div className="flex items-center">
                      <FaUniversity className="h-5 w-5 text-blue-600 mr-2" />
                      <h3 className="text-sm font-semibold text-blue-800">University Lab</h3>
                    </div>
                    <p className="text-xs text-blue-600 mt-1">Internal university laboratory</p>
                  </motion.div>

                  <motion.div
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.99 }}
                    className="bg-green-50 p-4 rounded-lg border border-green-200 cursor-pointer transition-all duration-200 hover:shadow-md hover:border-green-400"
                    onClick={() => handleLabTypeSelect('outside')}
                  >
                    <div className="flex items-center">
                      <FaBuilding className="h-5 w-5 text-green-600 mr-2" />
                      <h3 className="text-sm font-semibold text-green-800">Outside Lab</h3>
                    </div>
                    <p className="text-xs text-green-600 mt-1">External specialized lab</p>
                  </motion.div>
                </div>
              )}

              {/* University Lab Forms */}
              {labType === 'university' && !formType && (
                <div className="mb-6">
                  <h2 className="text-sm font-semibold text-gray-700 mb-3">Select Form Type</h2>
                  <div className="grid grid-cols-2 gap-3">
                    <motion.div
                      whileHover={{ scale: 1.01 }}
                      whileTap={{ scale: 0.99 }}
                      className="bg-gray-50 p-4 rounded-lg border border-gray-200 cursor-pointer transition-all duration-200 hover:shadow-md hover:border-[#0077B6]"
                      onClick={() => handleFormTypeSelect('fixed')}
                    >
                      <h3 className="text-sm font-semibold text-gray-800">Fixed Prosthodontics</h3>
                      <p className="text-xs text-gray-500 mt-1">Fixed procedures</p>
                    </motion.div>

                    <motion.div
                      whileHover={{ scale: 1.01 }}
                      whileTap={{ scale: 0.99 }}
                      className="bg-gray-50 p-4 rounded-lg border border-gray-200 cursor-pointer transition-all duration-200 hover:shadow-md hover:border-[#0077B6]"
                      onClick={() => handleFormTypeSelect('removable')}
                    >
                      <h3 className="text-sm font-semibold text-gray-800">Removable Prosthodontics</h3>
                      <p className="text-xs text-gray-500 mt-1">Removable procedures</p>
                    </motion.div>
                  </div>
                </div>
              )}

              {/* Fixed Prosthodontics Sub-forms */}
              {labType === 'university' && formType === 'fixed' && !subFormType && (
                <div className="mb-6">
                  <h2 className="text-sm font-semibold text-gray-700 mb-3">Select Fixed Prosthodontics Form</h2>
                  <div className="grid grid-cols-3 gap-3">
                    <motion.div
                      whileHover={{ scale: 1.01 }}
                      whileTap={{ scale: 0.99 }}
                      className="bg-blue-50 p-3 rounded-lg border border-blue-200 cursor-pointer transition-all duration-200 hover:shadow-md hover:border-blue-400"
                      onClick={() => handleSubFormTypeSelect('primary')}
                    >
                      <h3 className="text-sm font-semibold text-blue-800">1ry Impression</h3>
                      <p className="text-xs text-blue-600 mt-1">Primary impression</p>
                    </motion.div>

                    <motion.div
                      whileHover={{ scale: 1.01 }}
                      whileTap={{ scale: 0.99 }}
                      className="bg-blue-50 p-3 rounded-lg border border-blue-200 cursor-pointer transition-all duration-200 hover:shadow-md hover:border-blue-400"
                      onClick={() => handleSubFormTypeSelect('final')}
                    >
                      <h3 className="text-sm font-semibold text-blue-800">Final Restoration</h3>
                      <p className="text-xs text-blue-600 mt-1">Final form</p>
                    </motion.div>

                    <motion.div
                      whileHover={{ scale: 1.01 }}
                      whileTap={{ scale: 0.99 }}
                      className="bg-blue-50 p-3 rounded-lg border border-blue-200 cursor-pointer transition-all duration-200 hover:shadow-md hover:border-blue-400"
                      onClick={() => handleSubFormTypeSelect('tryin')}
                    >
                      <h3 className="text-sm font-semibold text-blue-800">Try In</h3>
                      <p className="text-xs text-blue-600 mt-1">Try in form</p>
                    </motion.div>
                  </div>
                </div>
              )}

              {/* Removable Prosthodontics Sub-forms */}
              {labType === 'university' && formType === 'removable' && !subFormType && (
                <div className="mb-6">
                  <h2 className="text-sm font-semibold text-gray-700 mb-3">Select Removable Prosthodontics Form</h2>
                  <div className="grid grid-cols-2 gap-3">
                    <motion.div
                      whileHover={{ scale: 1.01 }}
                      whileTap={{ scale: 0.99 }}
                      className="bg-green-50 p-3 rounded-lg border border-green-200 cursor-pointer transition-all duration-200 hover:shadow-md hover:border-green-400"
                      onClick={() => handleSubFormTypeSelect('complete')}
                    >
                      <h3 className="text-sm font-semibold text-green-800">Complete Denture</h3>
                      <p className="text-xs text-green-600 mt-1">Complete denture form</p>
                    </motion.div>

                    <motion.div
                      whileHover={{ scale: 1.01 }}
                      whileTap={{ scale: 0.99 }}
                      className="bg-green-50 p-3 rounded-lg border border-green-200 cursor-pointer transition-all duration-200 hover:shadow-md hover:border-green-400"
                      onClick={() => handleSubFormTypeSelect('rpd')}
                    >
                      <h3 className="text-sm font-semibold text-green-800">RPD</h3>
                      <p className="text-xs text-green-600 mt-1">Partial denture form</p>
                    </motion.div>
                  </div>
                </div>
              )}

              {/* Fixed Prosthodontics - Primary Impression Form */}
              {labType === 'university' && formType === 'fixed' && subFormType === 'primary' && (
                <div className="bg-gray-50 p-6 rounded-xl border border-gray-200">
                  <h2 className="text-xl font-semibold text-gray-800 mb-6">Laboratory Prescription Form - Fixed Prosthodontics – 1ry Impression</h2>
                  
                  <div className="grid md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Student Name:</label>
                      <input
                        type="text"
                        value={user?.name || ''}
                        disabled
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Student ID:</label>
                      <input
                        type="text"
                        value={user?.studentId || user?.id || ''}
                        disabled
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Patient Name:</label>
                      <input
                        type="text"
                        value={patientData.fullName || ''}
                        disabled
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Tooth:</label>
                      <input
                        type="text"
                        value={fixedPrimaryForm.tooth}
                        onChange={(e) => setFixedPrimaryForm({ ...fixedPrimaryForm, tooth: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0077B6]"
                        placeholder="Enter tooth number"
                      />
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Laboratory steps required:</label>
                    <div className="space-y-2">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={fixedPrimaryForm.laboratorySteps.mountedDiagnosticCasts}
                          onChange={(e) => setFixedPrimaryForm({
                            ...fixedPrimaryForm,
                            laboratorySteps: {
                              ...fixedPrimaryForm.laboratorySteps,
                              mountedDiagnosticCasts: e.target.checked
                            }
                          })}
                          className="mr-2"
                        />
                        <span>Mounted Diagnostic Casts</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={fixedPrimaryForm.laboratorySteps.diagnosticWaxUp}
                          onChange={(e) => setFixedPrimaryForm({
                            ...fixedPrimaryForm,
                            laboratorySteps: {
                              ...fixedPrimaryForm.laboratorySteps,
                              diagnosticWaxUp: e.target.checked
                            }
                          })}
                          className="mr-2"
                        />
                        <span>Diagnostic Wax up</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={fixedPrimaryForm.laboratorySteps.provisionalRestoration}
                          onChange={(e) => setFixedPrimaryForm({
                            ...fixedPrimaryForm,
                            laboratorySteps: {
                              ...fixedPrimaryForm.laboratorySteps,
                              provisionalRestoration: e.target.checked
                            }
                          })}
                          className="mr-2"
                        />
                        <span>Provisional Restoration</span>
                      </label>
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date:</label>
                    <input
                      type="text"
                      value={new Date().toLocaleDateString()}
                      disabled
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100"
                    />
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-1">NOTES:</label>
                    <textarea
                      value={fixedPrimaryForm.notes}
                      onChange={(e) => setFixedPrimaryForm({ ...fixedPrimaryForm, notes: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0077B6]"
                      rows="3"
                      placeholder="Enter notes..."
                    />
                  </div>

                  <button
                    onClick={handleSubmit}
                    disabled={submitting}
                    className="w-full md:w-auto px-6 py-2 bg-[#0077B6] text-white rounded-lg hover:bg-[#005A8B] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {submitting ? 'Submitting...' : 'Submit Request'}
                  </button>
                </div>
              )}

              {/* Fixed Prosthodontics - Final Restoration Form */}
              {labType === 'university' && formType === 'fixed' && subFormType === 'final' && (
                <div className="bg-gray-50 p-6 rounded-xl border border-gray-200">
                  <h2 className="text-xl font-semibold text-gray-800 mb-6">Laboratory Prescription Form - Fixed Prosthodontics – Final Restoration</h2>
                  
                  <div className="grid md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Student Name:</label>
                      <input
                        type="text"
                        value={user?.name || ''}
                        disabled
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Student ID:</label>
                      <input
                        type="text"
                        value={user?.studentId || user?.id || ''}
                        disabled
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Patient Name:</label>
                      <input
                        type="text"
                        value={patientData.fullName || ''}
                        disabled
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Tooth:</label>
                      <input
                        type="text"
                        value={fixedFinalForm.tooth}
                        onChange={(e) => setFixedFinalForm({ ...fixedFinalForm, tooth: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0077B6]"
                        placeholder="Enter tooth number"
                      />
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date:</label>
                    <input
                      type="text"
                      value={new Date().toLocaleDateString()}
                      disabled
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100"
                    />
                  </div>

                  <div className="grid md:grid-cols-3 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Design:</label>
                      <select
                        value={fixedFinalForm.design}
                        onChange={(e) => setFixedFinalForm({ ...fixedFinalForm, design: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0077B6]"
                      >
                        <option value="">Select Design</option>
                        <option value="Inlay/Onlay">Inlay/Onlay</option>
                        <option value="Crown">Crown</option>
                        <option value="Endocrown">Endocrown</option>
                        <option value="Veneer">Veneer</option>
                        <option value="Fixed Partial Denture">Fixed Partial Denture</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Material:</label>
                      <select
                        value={fixedFinalForm.material}
                        onChange={(e) => setFixedFinalForm({ ...fixedFinalForm, material: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0077B6]"
                      >
                        <option value="">Select Material</option>
                        <option value="Full metal">Full metal</option>
                        <option value="Porcelain fused to metal">Porcelain fused to metal</option>
                        <option value="Monolithic zirconia">Monolithic zirconia</option>
                        <option value="Porcelain fused to zirconia">Porcelain fused to zirconia</option>
                        <option value="Emax">Emax</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Pontic:</label>
                      <select
                        value={fixedFinalForm.pontic}
                        onChange={(e) => setFixedFinalForm({ ...fixedFinalForm, pontic: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0077B6]"
                      >
                        <option value="">Select Pontic</option>
                        <option value="Modified ridge lap">Modified ridge lap</option>
                        <option value="Ovate">Ovate</option>
                        <option value="Hygienic">Hygienic</option>
                        <option value="Conical">Conical</option>
                      </select>
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Color:</label>
                    <input
                      type="text"
                      value={fixedFinalForm.color}
                      onChange={(e) => setFixedFinalForm({ ...fixedFinalForm, color: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0077B6]"
                      placeholder="Enter color"
                    />
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-1">NOTES:</label>
                    <textarea
                      value={fixedFinalForm.notes}
                      onChange={(e) => setFixedFinalForm({ ...fixedFinalForm, notes: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0077B6]"
                      rows="3"
                      placeholder="Enter notes..."
                    />
                  </div>

                  <button
                    onClick={handleSubmit}
                    disabled={submitting}
                    className="w-full md:w-auto px-6 py-2 bg-[#0077B6] text-white rounded-lg hover:bg-[#005A8B] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {submitting ? 'Submitting...' : 'Submit Request'}
                  </button>
                </div>
              )}

              {/* Fixed Prosthodontics - Try In Form */}
              {labType === 'university' && formType === 'fixed' && subFormType === 'tryin' && (
                <div className="bg-gray-50 p-6 rounded-xl border border-gray-200">
                  <h2 className="text-xl font-semibold text-gray-800 mb-6">Laboratory Prescription Form - Fixed Prosthodontics – Try in</h2>
                  
                  <div className="grid md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Student Name:</label>
                      <input
                        type="text"
                        value={user?.name || ''}
                        disabled
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Student ID:</label>
                      <input
                        type="text"
                        value={user?.studentId || user?.id || ''}
                        disabled
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Patient Name:</label>
                      <input
                        type="text"
                        value={patientData.fullName || ''}
                        disabled
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Tooth:</label>
                      <input
                        type="text"
                        value={fixedTryInForm.tooth}
                        onChange={(e) => setFixedTryInForm({ ...fixedTryInForm, tooth: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0077B6]"
                        placeholder="Enter tooth number"
                      />
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date:</label>
                    <input
                      type="text"
                      value={new Date().toLocaleDateString()}
                      disabled
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100"
                    />
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Status:</label>
                    <div className="space-y-2">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={fixedTryInForm.tryInOk}
                          onChange={(e) => setFixedTryInForm({
                            ...fixedTryInForm,
                            tryInOk: e.target.checked,
                            problemWithTryIn: e.target.checked ? false : fixedTryInForm.problemWithTryIn
                          })}
                          className="mr-2"
                        />
                        <span>Try in OK</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={fixedTryInForm.problemWithTryIn}
                          onChange={(e) => setFixedTryInForm({
                            ...fixedTryInForm,
                            problemWithTryIn: e.target.checked,
                            tryInOk: e.target.checked ? false : fixedTryInForm.tryInOk
                          })}
                          className="mr-2"
                        />
                        <span>Problem with try in</span>
                      </label>
                    </div>
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Remarks:</label>
                    <textarea
                      value={fixedTryInForm.remarks}
                      onChange={(e) => setFixedTryInForm({ ...fixedTryInForm, remarks: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0077B6]"
                      rows="3"
                      placeholder="Enter remarks..."
                    />
                  </div>

                  <button
                    onClick={handleSubmit}
                    disabled={submitting}
                    className="w-full md:w-auto px-6 py-2 bg-[#0077B6] text-white rounded-lg hover:bg-[#005A8B] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {submitting ? 'Submitting...' : 'Submit Request'}
                  </button>
                </div>
              )}

              {/* Removable Prosthodontics - Complete Denture Form */}
              {labType === 'university' && formType === 'removable' && subFormType === 'complete' && (
                <div className="bg-gray-50 p-6 rounded-xl border border-gray-200">
                  <h2 className="text-xl font-semibold text-gray-800 mb-6">Laboratory Prescription Form - Removable Prosthodontics – Complete Denture</h2>
                  
                  <div className="grid md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Student Name:</label>
                      <input
                        type="text"
                        value={user?.name || ''}
                        disabled
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Student ID:</label>
                      <input
                        type="text"
                        value={user?.studentId || user?.id || ''}
                        disabled
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100"
                      />
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Patient Name:</label>
                    <input
                      type="text"
                      value={patientData.fullName || ''}
                      disabled
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100"
                    />
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date:</label>
                    <input
                      type="text"
                      value={new Date().toLocaleDateString()}
                      disabled
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100"
                    />
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Laboratory steps required:</label>
                    <div className="space-y-2">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={removableCompleteForm.laboratorySteps.studyCastsAndCustomTrays}
                          onChange={(e) => setRemovableCompleteForm({
                            ...removableCompleteForm,
                            laboratorySteps: {
                              ...removableCompleteForm.laboratorySteps,
                              studyCastsAndCustomTrays: e.target.checked
                            }
                          })}
                          className="mr-2"
                        />
                        <span>Study casts and Custom trays</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={removableCompleteForm.laboratorySteps.masterCastsAndRecordBlocks}
                          onChange={(e) => setRemovableCompleteForm({
                            ...removableCompleteForm,
                            laboratorySteps: {
                              ...removableCompleteForm.laboratorySteps,
                              masterCastsAndRecordBlocks: e.target.checked
                            }
                          })}
                          className="mr-2"
                        />
                        <span>Master casts and record blocks</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={removableCompleteForm.laboratorySteps.settingOfTeeth}
                          onChange={(e) => setRemovableCompleteForm({
                            ...removableCompleteForm,
                            laboratorySteps: {
                              ...removableCompleteForm.laboratorySteps,
                              settingOfTeeth: e.target.checked
                            }
                          })}
                          className="mr-2"
                        />
                        <span>Setting of teeth on mounted master casts (mean value articulator)</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={removableCompleteForm.laboratorySteps.finalProcessedPolishedDenture}
                          onChange={(e) => setRemovableCompleteForm({
                            ...removableCompleteForm,
                            laboratorySteps: {
                              ...removableCompleteForm.laboratorySteps,
                              finalProcessedPolishedDenture: e.target.checked
                            }
                          })}
                          className="mr-2"
                        />
                        <span>Final processed polished denture</span>
                      </label>
                    </div>
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-1">NOTES:</label>
                    <textarea
                      value={removableCompleteForm.notes}
                      onChange={(e) => setRemovableCompleteForm({ ...removableCompleteForm, notes: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0077B6]"
                      rows="3"
                      placeholder="Enter notes..."
                    />
                  </div>

                  <button
                    onClick={handleSubmit}
                    disabled={submitting}
                    className="w-full md:w-auto px-6 py-2 bg-[#0077B6] text-white rounded-lg hover:bg-[#005A8B] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {submitting ? 'Submitting...' : 'Submit Request'}
                  </button>
                </div>
              )}

              {/* Removable Prosthodontics - RPD Form */}
              {labType === 'university' && formType === 'removable' && subFormType === 'rpd' && (
                <div className="bg-gray-50 p-6 rounded-xl border border-gray-200">
                  <h2 className="text-xl font-semibold text-gray-800 mb-6">Laboratory Prescription Form - Removable Prosthodontics – RPD</h2>
                  
                  <div className="grid md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Student Name:</label>
                      <input
                        type="text"
                        value={user?.name || ''}
                        disabled
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Student ID:</label>
                      <input
                        type="text"
                        value={user?.studentId || user?.id || ''}
                        disabled
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100"
                      />
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Patient Name:</label>
                    <input
                      type="text"
                      value={patientData.fullName || ''}
                      disabled
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100"
                    />
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date:</label>
                    <input
                      type="text"
                      value={new Date().toLocaleDateString()}
                      disabled
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100"
                    />
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Laboratory steps required:</label>
                    <div className="space-y-2">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={removableRpdForm.laboratorySteps.studyCastsAndCustomTrays}
                          onChange={(e) => setRemovableRpdForm({
                            ...removableRpdForm,
                            laboratorySteps: {
                              ...removableRpdForm.laboratorySteps,
                              studyCastsAndCustomTrays: e.target.checked
                            }
                          })}
                          className="mr-2"
                        />
                        <span>Study casts and Custom trays</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={removableRpdForm.laboratorySteps.masterCastsAndMetalFramework}
                          onChange={(e) => setRemovableRpdForm({
                            ...removableRpdForm,
                            laboratorySteps: {
                              ...removableRpdForm.laboratorySteps,
                              masterCastsAndMetalFramework: e.target.checked
                            }
                          })}
                          className="mr-2"
                        />
                        <span>Master casts and metal framework</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={removableRpdForm.laboratorySteps.settingOfTeeth}
                          onChange={(e) => setRemovableRpdForm({
                            ...removableRpdForm,
                            laboratorySteps: {
                              ...removableRpdForm.laboratorySteps,
                              settingOfTeeth: e.target.checked
                            }
                          })}
                          className="mr-2"
                        />
                        <span>Setting of teeth on mounted master casts (mean value articulator)</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={removableRpdForm.laboratorySteps.rpdTryIn}
                          onChange={(e) => setRemovableRpdForm({
                            ...removableRpdForm,
                            laboratorySteps: {
                              ...removableRpdForm.laboratorySteps,
                              rpdTryIn: e.target.checked
                            }
                          })}
                          className="mr-2"
                        />
                        <span>RPD try-in</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={removableRpdForm.laboratorySteps.finalProcessedPolishedDenture}
                          onChange={(e) => setRemovableRpdForm({
                            ...removableRpdForm,
                            laboratorySteps: {
                              ...removableRpdForm.laboratorySteps,
                              finalProcessedPolishedDenture: e.target.checked
                            }
                          })}
                          className="mr-2"
                        />
                        <span>Final processed polished denture</span>
                      </label>
                    </div>
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-1">NOTES:</label>
                    <textarea
                      value={removableRpdForm.notes}
                      onChange={(e) => setRemovableRpdForm({ ...removableRpdForm, notes: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0077B6]"
                      rows="3"
                      placeholder="Enter notes..."
                    />
                  </div>

                  <button
                    onClick={handleSubmit}
                    disabled={submitting}
                    className="w-full md:w-auto px-6 py-2 bg-[#0077B6] text-white rounded-lg hover:bg-[#005A8B] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {submitting ? 'Submitting...' : 'Submit Request'}
                  </button>
                </div>
              )}

              </div>

              {/* Outside Lab - Simple form */}
              {labType === 'outside' && (
                <div className="p-4 sm:p-6">
                <div className="bg-gray-50 p-6 rounded-xl border border-gray-200">
                  <h2 className="text-xl font-semibold text-gray-800 mb-6">Outside Lab Request</h2>
                  
                  <div className="grid md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Student Name:</label>
                      <input
                        type="text"
                        value={user?.name || ''}
                        disabled
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Student ID:</label>
                      <input
                        type="text"
                        value={user?.studentId || user?.id || ''}
                        disabled
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100"
                      />
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Patient Name:</label>
                    <input
                      type="text"
                      value={patientData.fullName || ''}
                      disabled
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100"
                    />
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Notes:</label>
                    <textarea
                      value={fixedPrimaryForm.notes}
                      onChange={(e) => setFixedPrimaryForm({ ...fixedPrimaryForm, notes: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0077B6]"
                      rows="4"
                      placeholder="Enter notes for outside lab request..."
                    />
                  </div>

                  <button
                    onClick={async () => {
                      setSubmitting(true);
                      try {
                        const requestData = {
                          patientId: nationalId,
                          patientName: patientData.fullName,
                          labType: 'outside',
                          notes: fixedPrimaryForm.notes,
                          studentId: user?.studentId || user?.id,
                          studentName: user?.name
                        };
                        await axios.post(`${process.env.REACT_APP_API_URL}/api/lab-requests`, requestData, {
                          headers: { Authorization: `Bearer ${token}` }
                        });
                        setShowSuccess(true);
                        setTimeout(() => {
                          setShowSuccess(false);
                          resetForm();
                          fetchLabRequests();
                        }, 2000);
                      } catch (error) {
                        console.error('Error submitting lab request:', error);
                        alert('Error submitting lab request. Please try again.');
                      } finally {
                        setSubmitting(false);
                      }
                    }}
                    disabled={submitting}
                    className="w-full md:w-auto px-6 py-2 bg-[#0077B6] text-white rounded-lg hover:bg-[#005A8B] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {submitting ? 'Submitting...' : 'Submit Request'}
                  </button>
                </div>
                </div>
              )}

            </motion.div>

            {/* Lab Requests History */}
            {labRequests.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="mt-6 bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden"
              >
                <div className="p-4 sm:p-6 border-b border-gray-100">
                  <h2 className="text-lg font-semibold text-gray-800">Request History</h2>
                </div>
                <div className="p-4 sm:p-6">
                  <div className="space-y-3">
                    {labRequests.map((request) => (
                      <motion.div
                        key={request.id || request._id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center">
                            {request.labType === 'university' ? (
                              <FaUniversity className="h-5 w-5 text-blue-600 mr-2" />
                            ) : (
                              <FaBuilding className="h-5 w-5 text-green-600 mr-2" />
                            )}
                            <span className="font-medium text-gray-800">
                              {request.labType === 'university' ? 'University Lab' : 'Outside Lab'}
                              {request.formData?.formType && ` - ${request.formData.formType.replace(/_/g, ' ')}`}
                            </span>
                          </div>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                            request.status === 'pending' ? 'bg-orange-100 text-orange-600' :
                            request.status === 'approved' ? 'bg-green-100 text-green-600' :
                            request.status === 'rejected' ? 'bg-red-100 text-red-600' :
                            'bg-blue-100 text-blue-600'
                          }`}>
                            {request.status ? request.status.charAt(0).toUpperCase() + request.status.slice(1) : 'Unknown'}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mb-1">
                          <span className="font-medium">Submitted:</span> {formatDateString(request.submitDate)}
                        </p>
                        {request.formData && (
                          <div className="mt-2 p-2 bg-gray-50 rounded text-sm">
                            {request.formData.formType && (
                              <p className="font-medium mb-1">Form Type: {request.formData.formType.replace(/_/g, ' ')}</p>
                            )}
                            {request.formData.tooth && (
                              <p>Tooth: {request.formData.tooth}</p>
                            )}
                            {request.formData.design && (
                              <p>Design: {request.formData.design}</p>
                            )}
                            {request.formData.material && (
                              <p>Material: {request.formData.material}</p>
                            )}
                            {request.formData.color && (
                              <p>Color: {request.formData.color}</p>
                            )}
                            {request.formData.notes && (
                              <p className="mt-1">Notes: {request.formData.notes}</p>
                            )}
                          </div>
                        )}
                        {request.notes && !request.formData && (
                          <p className="text-sm text-gray-600">
                            <span className="font-medium">Notes:</span> {request.notes}
                          </p>
                        )}
                        {request.responseNotes && (
                          <div className="mt-2 p-2 bg-blue-50 rounded text-sm">
                            <p className="font-medium">Response: {request.responseNotes}</p>
                            {request.responseDate && (
                              <p className="text-xs text-gray-500 mt-1">
                                Responded on: {formatDateString(request.responseDate)}
                              </p>
                            )}
                          </div>
                        )}
                      </motion.div>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default withRoleGuard(['student'])(PatientLab);

